import { z } from 'zod';
import { localDateSchema } from './common';

/**
 * `RecurrenceRule` (PLAN §3.2 `task_recurrences`, D-057): the schedule of a recurring one-off task
 * ("Send AR ageing to CFO every Monday"), stored in `task_recurrences.rule` and offered to meeting
 * series. Workspace-local dates only; `@orbit/domain/recurrence` `expandRecurrence(rule, dtstart,
 * from, to)` turns it into occurrence dates, and the non-working-day rule is applied separately.
 * Strict: unknown keys are rejected.
 *
 * - `daily` every `interval` days; `weekdays` on `byWeekday` (default Monday–Saturday, D-031);
 *   `weekly` every `interval` weeks on `byWeekday` (default the weekday of `dtstart`);
 * - `monthly` / `quarterly` / `yearly` on `byMonthDay` (1–31, or −1 = the last day; a day the
 *   month does not have falls on its last day) or `nthWeekday` ("first Monday", "last Friday");
 *   default the day of month of `dtstart`. `byMonth` is the month for `yearly` and the first month
 *   of the cycle for `quarterly` (default the month of `dtstart`);
 * - `until` (inclusive) and `count` (occurrences from `dtstart`) end the series.
 */

export const RECURRENCE_FREQUENCIES = [
  'daily',
  'weekdays',
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
] as const;
export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export const isoWeekdaySchema = z.int().min(1).max(7);

export const recurrenceRuleSchema = z
  .strictObject({
    freq: z.enum(RECURRENCE_FREQUENCIES),
    interval: z.int().min(1).max(366).default(1),
    byWeekday: z
      .array(isoWeekdaySchema)
      .min(1)
      .max(7)
      .refine((days) => new Set(days).size === days.length, 'Weekdays must be distinct')
      .optional(),
    byMonthDay: z
      .int()
      .refine((d) => d === -1 || (d >= 1 && d <= 31), 'Expected 1..31 or -1')
      .optional(),
    nthWeekday: z
      .strictObject({
        n: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(-1)]),
        weekday: isoWeekdaySchema,
      })
      .optional(),
    byMonth: z.int().min(1).max(12).optional(),
    until: localDateSchema.optional(),
    count: z.int().min(1).max(1000).optional(),
  })
  .superRefine((rule, ctx) => {
    const weekly = rule.freq === 'weekdays' || rule.freq === 'weekly';
    const monthly = rule.freq === 'monthly' || rule.freq === 'quarterly' || rule.freq === 'yearly';
    if (rule.byWeekday && !weekly) {
      ctx.addIssue({
        code: 'custom',
        path: ['byWeekday'],
        message: 'Only for weekdays and weekly',
      });
    }
    if ((rule.byMonthDay !== undefined || rule.nthWeekday) && !monthly) {
      ctx.addIssue({
        code: 'custom',
        path: [rule.byMonthDay !== undefined ? 'byMonthDay' : 'nthWeekday'],
        message: 'Only for monthly, quarterly and yearly',
      });
    }
    if (rule.byMonthDay !== undefined && rule.nthWeekday) {
      ctx.addIssue({
        code: 'custom',
        path: ['nthWeekday'],
        message: 'Give byMonthDay or nthWeekday, not both',
      });
    }
    if (rule.byMonth !== undefined && rule.freq !== 'yearly' && rule.freq !== 'quarterly') {
      ctx.addIssue({ code: 'custom', path: ['byMonth'], message: 'Only for yearly and quarterly' });
    }
    if (rule.freq === 'weekdays' && rule.interval !== 1) {
      ctx.addIssue({ code: 'custom', path: ['interval'], message: 'weekdays repeats every week' });
    }
    if (rule.until !== undefined && rule.count !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['count'], message: 'Give until or count, not both' });
    }
  });

export type RecurrenceRule = z.infer<typeof recurrenceRuleSchema>;
/** As sent (before defaults). */
export type RecurrenceRuleInput = z.input<typeof recurrenceRuleSchema>;
